import React, {FC} from "react"
import * as PropTypes from "prop-types"
import {isNull} from "lodash"

import {FormattedMessage, FormattedCurrency} from "@irati/utils/lang"
import {withTheme, WithDefaultTheme} from "styled-components"
import {Row} from "react-bootstrap"
import {PaymentCurrencyType, WalletEntityType} from "@irati/types"
import Flex from "@irati/ui/src/Flex"
import {H2, H6, Label} from "@irati/ui/src/Typography"
import Button from "@irati/ui/src/Button"
import {Input, ValidationButton} from "@irati/ui/src/FormItem"
import Validation from "@irati/utils/validation"
import RecipientList from "@irati-web/reward/src/components/Checkout/Steps/Recipients/RecipientList"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import WalletBallance from "@irati/wallet/apollo/query/Wallet"
import authentication from "@irati/auth"
import History from "@irati/utils/history"
import {Card} from "@irati/ui/src/Layout"
import Spacing from "@irati/ui/src/Spacing"
import {noDecimal} from "@irati/utils/validation/regex/number"
import {RouterLink} from "@irati/ui/src/Link"
import {DistributePayload} from "../../../containers/AdminReward/DistributeReward"
import {StepNames} from "../../../enums"

interface Props extends WithDefaultTheme {
  payload: DistributePayload,
  handlePrevStep: () => void,
  handlePointsPerUser: (pointsPerUser: number) => void
}

const MIN_AMOUNT = 5

const distributeRewardPointsSchema = (pointsInWallet: number, numberOfUsers: number) => {
  return Validation.string("wallet.reward.admin.distribute.validation.pointsPerUser.required")
    .matches(noDecimal, "wallet.reward.admin.distribute.validation.integersOnly")
    .test(
      "Positive, value above 5 and no decimal",
      "wallet.reward.admin.distribute.validation.pointsPerUser.min",
      (val) => val >= MIN_AMOUNT || isNull(val)
    )
    .test(
      "Value cannot exceed wallet total",
      "wallet.reward.admin.distribute.validation.pointsPerUser.max",
      (val) => val * numberOfUsers <= pointsInWallet)
}

const DistributeRewardSummaryStep1: FC<Props> = ({
  theme,
  payload,
  handlePrevStep,
  handlePointsPerUser
}, context) => {

  const nextStep = () => {
    if (location.pathname.includes(StepNames.Recipients)) {
      History.push(`/flexi-assign/${StepNames.Message}`, context)
    } else {
      History.push(`/flexi-assign/${StepNames.Confirm}`, context)
    }
  }

  return (
    <>
      <Card flush>
        <H6 paddingLeft={3} paddingTop={4}>
          <FormattedMessage id="wallet.reward.admin.summary.title" />
        </H6>
        <Flex direction="column" padding={3}>
          <Label large>
            <FormattedMessage id="wallet.reward.admin.distribute.enterPoints.title" />
          </Label>
          <WalletBallance
            variables={{userId: authentication.user().uuid, currency: PaymentCurrencyType.Rp, entityType: WalletEntityType.Reward}}>
            {(apollo) => {
              return <Row>
                <Input
                  name="pointsPerUser"
                  type="number"
                  min="5"
                  value={payload.pointsPerUser}
                  placeholder="wallet.reward.admin.distribute.enterPoints.placeholder"
                  validation={distributeRewardPointsSchema(Math.round(apollo.data.wallet.amount / 100), payload.destinationUsers.length)}
                  onChange={handlePointsPerUser} />
              </Row>
            }}
          </WalletBallance>
        </Flex>

        {!!payload.destinationUsers.length &&
        <Spacing paddingLeft={3} paddingRight={3} marginBottom={2}>
          <Spacing marginBottom>
            <Label large>
              <FormattedMessage id="wallet.reward.admin.distribute.recipients" />
            </Label>
          </Spacing>
          <RecipientList recipients={payload.destinationUsers} />
        </Spacing>}

        <Flex style={{background: theme.colors.utility.highlight}} justifyContent="center" paddingTop={2} paddingBottom={2}>
          <Flex direction="column" alignItems="center">
            <Label small marginBottom={0.5}>
              <FormattedMessage id="wallet.reward.intro.available" />
            </Label>
            <WalletBallance
              variables={{userId: authentication.user().uuid, currency: PaymentCurrencyType.Rp, entityType: WalletEntityType.Reward}}>
              {(apollo) => {
                return <>
                  <FormattedCurrency value={apollo.data.wallet.amount} currency={PaymentCurrencyType.Rp} />
                  {!!authentication.apps("wallet.flexiRewards.canTopUpRewardPoints") &&
                  payload.pointsPerUser * payload.destinationUsers.length > apollo.data.wallet.amount / 100 &&
                  <RouterLink to="/flexi-checkout/top-up" marginTop>
                    <FormattedMessage id="wallet.reward.intro.topup" />
                  </RouterLink>
                  }
                </>
              }}
            </WalletBallance>
          </Flex>
        </Flex>

        <Flex justifyContent="space-between" alignItems="center" padding={3}>
          <Label large>
            <FormattedMessage id="wallet.reward.admin.card.topup.totalToPay" />
          </Label>
          <Flex alignItems="center">
            <Icon icon={IconTypes.rewardPoints} size={32} />
            <WalletBallance
              variables={{userId: authentication.user().uuid, currency: PaymentCurrencyType.Rp, entityType: WalletEntityType.Reward}}>
              {(apollo) => (
                payload.pointsPerUser * payload.destinationUsers.length > apollo.data.wallet.amount / 100 ?
                  <H2 marginLeft={1} color={theme.colors.tertiary.r_400}>{payload.pointsPerUser * payload.destinationUsers.length}</H2> :
                  <H2 marginLeft={1}>{payload.pointsPerUser * payload.destinationUsers.length}</H2>
              )}
            </WalletBallance>
          </Flex>
        </Flex>

        <Flex justifyContent="space-between" paddingLeft={3} paddingRight={3} paddingBottom={3}>
          {!location.pathname.includes(StepNames.Recipients) && (
            <Button onClick={handlePrevStep} marginRight>
              <FormattedMessage id="platform.button.back" />
            </Button>
          )}

          <ValidationButton full primary onClick={nextStep}>
            <FormattedMessage id="platform.button.continue" />
          </ValidationButton>
        </Flex>
      </Card>
    </>
  )
}

DistributeRewardSummaryStep1.contextTypes = {
  appPath: PropTypes.string.isRequired
}

export default withTheme(DistributeRewardSummaryStep1)
